import { useState, useEffect, ChangeEvent } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { pdf } from '@react-pdf/renderer';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Select,
  MenuItem,
  Checkbox,
  Stack,
  Container,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUserSettings } from '@/hooks/useUserSettings';
import { DumbFormSelect } from '@/components/uiComponents/FormSelect';
import { useAdsData } from '@/hooks/useAdsData';
import { styled, shouldForwardProp } from '@mui/system';
import classNames from 'classnames';
import { AdMediaType, UserKindEnum, State } from '@/types';
import { MyButton, Loader, SectionTitle, MyDialog, LoaderOverlay } from '@/components';
import { FormCheckboxWithWatch } from '@/components/uiComponents/FormCheckbox/FormCheckboxWithWatch';
import { isScrollable } from '@dnd-kit/core/dist/utilities/scroll';
import { GalleryBlock } from './GalleryBlock';
import { GeneratePDF } from './GeneratePDF';
import { convertToPNG } from '@/components/uiComponents/FileUploader/FileUploader.utils';
import { documentSave } from '@/components/PdfManager/AdsPdfManager';

type SelectedImageInfo = {
  id: string;
  selected: AdMediaType[];
};

const currencyData = [
  {
    value: 'AED',
    label: 'AED',
  },
  {
    value: 'USD',
    label: 'USD',
  },
];

const boxStyle = {
  display: 'flex',
  gap: '20px',
  marginTop: '12px',
  flexDirection: {
    xs: 'column',
    sm: 'row',
  },
};

export const SavePdfDialog = ({
  open,
  setOpenDialog,
  selectedAds,
}: {
  open: boolean;
  setOpenDialog: (value: boolean) => void;
  selectedAds: string[];
}) => {
  const user = useSelector(({ auth }: State) => auth.user);
  const [isLoading, setIsLoading] = useState(false);
  const [currency, setCurrency] = useState('AED');
  const [isViewPdfVisible, setIsViewPdfVisible] = useState(false);
  const [showMyContactsCheckbox, setShowMyContactsCheckbox] = useState(true);
  const [selectedImageCounts, setSelectedImageCounts] = useState<SelectedImageInfo[]>([]);
  const { adsData, images } = useAdsData(selectedAds, currency, true);
  const [pdfFile, setPdfFile] = useState<Blob | null>(null);
  const [convertedImages, setConvertedImages] = useState<string[]>([]);
  const hideViewPdf = () => setIsViewPdfVisible(false);
  const form = useForm({
    defaultValues: {
      radioOption: 'portable-view',
      currency: 'AED',
    },
  });
  const { handleSubmit, reset } = form;

  const allValid =
    images.length > 0 &&
    images.every((img) => {
      const found = selectedImageCounts.find((i) => i.id === img.id);
      return found && found.selected.length >= 4;
    });

  const updateSelectedImages = (adId: string, selected: AdMediaType[]) => {
    setSelectedImageCounts((prev) => {
      const updated = [...prev];
      const index = updated.findIndex((item) => item.id === adId);
      if (index > -1) {
        updated[index] = { id: adId, selected };
      } else {
        updated.push({ id: adId, selected });
      }
      return updated;
    });
  };

  useEffect(() => {
    if (!user || !adsData) return;

    if (user.kind === UserKindEnum.Agency) {
      setShowMyContactsCheckbox(false);
    }
  }, [user, adsData]);

  const checkboxOptions = [
    {
      label: 'Show my contacts',
      name: 'brokerDetailsShow',
      showCheckbox: showMyContactsCheckbox,
      checkedByDefault: true,
    },
    {
      label: 'Show agency',
      name: 'agencyDetailsShow',
      showCheckbox: true,
      checkedByDefault: false,
    },
    {
      label: 'Show price',
      name: 'showPrice',
      showCheckbox: true,
      checkedByDefault: true,
    },
  ];

  const convertImages = async (images: AdMediaType[]) => {
    const promises = images.map(async (image) => {
      if (image.fileUrl) {
        try {
          const response = await fetch(image.fileUrl);
          const blob = await response.blob();
          const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
          const pngFile = await convertToPNG(file);
          if (pngFile) {
            return URL.createObjectURL(pngFile);
          }
        } catch (error) {
          console.error('Error converting image to PNG:', error);
        }
      }
      return null;
    });

    const results = await Promise.all(promises);
    const convertedPhoto = results.filter((url) => url !== null) as string[];
    setConvertedImages(convertedPhoto);
    return convertedPhoto;
  };

  const prepareAdsDataWithGallery = async () => {
    const newAds = await Promise.all(
      adsData.map(async (ads) => {
        const selectedImages = selectedImageCounts.find((image) => image.id === ads.id);
        const gallery = selectedImages ? await convertImages(selectedImages.selected) : [];
        return { ...ads, gallery };
      })
    );
    return newAds;
  };

  const onSubmit = async (data: any) => {
    if (['AED', 'USD'].includes(data?.currency)) {
      setCurrency(data?.currency);
    }
    setIsLoading(true);
    const newAdsData = await prepareAdsDataWithGallery();
    const blob = await pdf(
      <GeneratePDF
        adsData={newAdsData}
        personalInfo={user}
        showContent={{
          isAgencyInformationVisible: data.agencyDetailsShow,
          isBrokersInformationVisible: showMyContactsCheckbox
            ? data.brokerDetailsShow
            : data.agencyDetailsShow,
          showPrice: data.showPrice,
        }}
      />
    ).toBlob();
    const res = await documentSave(blob, `${adsData[0].fakeId}`, setPdfFile);
    setIsViewPdfVisible(true);
    if (res) setIsLoading(false);
  };

  const handleClose = () => {
    reset();
    setSelectedImageCounts([]);
    setOpenDialog(false);
  };

  return (
    <MyDialog
      open={open}
      width='900'
      onClose={handleClose}
      dialogTitle='Save as PDF'
      data-qa={'save_pdf_dialog'}
    >
      {isLoading && <LoaderOverlay />}
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} onChange={() => isViewPdfVisible && hideViewPdf()}>
          <Stack sx={{ marginTop: '10px', gap: '20px' }}>
            <SectionTitle variant='blank' number={1} title='PDF settings' />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0 28px' }}>
              <Stack
                sx={{
                  flexGrow: 1,
                  maxWidth: { xs: '100%', sm: '50%', md: '25%', lg: '20%' },
                }}
              >
                <Typography component='label' sx={{ mb: 1, color: '#2A3842', textAlign: 'left' }}>
                  Currency:
                </Typography>
                <Controller
                  control={form.control}
                  name='currency'
                  render={({ field }) => (
                    <DumbFormSelect
                      {...field}
                      defaultSelectedValue={currency}
                      placeholder='Currency'
                      disabled={false}
                      data={currencyData}
                      handleChange={(val) => {
                        if (['AED', 'USD'].includes(val)) {
                          setCurrency(val);
                          field.onChange(val);
                          hideViewPdf();
                        }
                      }}
                    />
                  )}
                />
              </Stack>
              {checkboxOptions.map((option, i) =>
                option.showCheckbox ? (
                  <FormCheckboxWithWatch
                    checked={option.checkedByDefault}
                    label={option.label}
                    name={option.name}
                    key={option.name + i}
                  />
                ) : null
              )}
            </Box>
          </Stack>
          <Stack sx={{ marginTop: '30px', gap: '10px' }}>
            <SectionTitle variant='blank' number={2} title='Select image' />
            {images.map((image) => (
              <GalleryBlock
                key={image.id}
                image={image}
                updateSelectedImages={updateSelectedImages}
              />
            ))}
          </Stack>
          <Box sx={boxStyle}>
            {isViewPdfVisible ? (
              <MyButton
                onClick={() => pdfFile && window.open(URL.createObjectURL(pdfFile))}
                data={{
                  customWidth: '226px',
                  buttonName: 'View PDF',
                  variant: 'contained',
                  dataQa: 'view_btn',
                }}
              />
            ) : (
              <MyButton
                data={{
                  customWidth: '226px',
                  buttonName: 'Save',
                  variant: 'contained',
                  buttonType: 'submit',
                  dataQa: 'save_btn',
                }}
                disabled={!allValid}
              />
            )}
            <MyButton
              data={{
                customWidth: '226px',
                buttonName: 'Cancel',
                variant: 'outlined',
                styleType: 'cancel',
                dataQa: 'cancel_btn',
              }}
              onClick={handleClose}
            />
          </Box>
        </form>
      </FormProvider>
    </MyDialog>
  );
};
